import React from "react"
import { useNavigate } from "react-router-dom"
import { OrderWorkflowSearchHit } from "lib/types/generated/graphql"
import dayjs from "dayjs"

import {
  Container,
  DraftIndicator,
  EmptyList,
  ExecutionCount,
  FailedCount,
  IdCell,
  IdText,
  Name,
  StatusBadge,
  SuccessCount,
  TriggerBadge,
  Wrapper
} from "./WorkflowTable.styled"

import TableRow from "components/Ui/Table/TableRow"
import TableHeader from "components/Ui/Table/TableHeader"
import TableBlurLoading from "components/Ui/Table/TableBlurLoading"
import ErrorMessage from "components/Ui/Messages/ErrorMessage"
import Status from "components/Ui/Status"
import CopyButton from "components/Ui/CopyButton"

import { ReactComponent as WorkflowIcon } from "images/icons/circle-nodes.svg"

type ExecutionCountData = { total: number; completed: number; failed: number }

type Props = {
  workflows: OrderWorkflowSearchHit[]
  loading: boolean
  error: Error | undefined
  publishedMap: Map<string, number>
  statusLoading: boolean
  executionCounts: Map<string, ExecutionCountData>
  countsLoading: boolean
}

function WorkflowTable({
  workflows,
  loading,
  error,
  publishedMap,
  statusLoading,
  executionCounts,
  countsLoading
}: Props) {
  const navigate = useNavigate()

  const tableHeader = (
    <TableHeader iconHeader>
      <div>Name</div>
      <div className="hideOnSmallScreens">ID</div>
      <div>Trigger</div>
      <div>Status</div>
      <div className="hideOnSmallScreens">Executions</div>
      <div className="hideOnSmallScreens">Revision</div>
      <div className="hideOnSmallScreens">Updated</div>
    </TableHeader>
  )

  const getTableRow = (workflow?: OrderWorkflowSearchHit) => {
    if (workflow) {
      return (
        <TableRow
          key={workflow.id}
          handleOnClick={() => navigate(`/settings/workflows/${workflow.id}`)}
        >
          <WorkflowIcon />
          <div>
            <Name>{workflow.name}</Name>
          </div>
          <div className="hideOnSmallScreens">
            <IdCell>
              <IdText title={workflow.id}>{workflow.id}</IdText>
              <CopyButton string={workflow.id} />
            </IdCell>
          </div>
          <div>
            <TriggerBadge>{workflow.trigger}</TriggerBadge>
          </div>
          <div>
            {statusLoading ? (
              <StatusBadge>...</StatusBadge>
            ) : (() => {
              const publishedRevision = publishedMap.get(workflow.id)
              const isPublished = publishedRevision !== undefined
              const hasDraft = isPublished && workflow.revision > publishedRevision
              return (
                <>
                  <StatusBadge $published={isPublished}>
                    {isPublished ? "Published" : "Draft"}
                  </StatusBadge>
                  {hasDraft && (
                    <DraftIndicator>v{workflow.revision} draft</DraftIndicator>
                  )}
                </>
              )
            })()}
          </div>
          <div className="hideOnSmallScreens">
            {countsLoading ? (
              <ExecutionCount>...</ExecutionCount>
            ) : (() => {
              const counts = executionCounts.get(workflow.id)
              if (!counts || counts.total === 0) return <ExecutionCount>—</ExecutionCount>
              return (
                <ExecutionCount>
                  {counts.total}{" "}
                  <SuccessCount>✓{counts.completed}</SuccessCount>{" "}
                  <FailedCount>✗{counts.failed}</FailedCount>
                </ExecutionCount>
              )
            })()}
          </div>
          <div className="hideOnSmallScreens">
            <Status status={`v${workflow.revision}`} />
          </div>
          <div className="hideOnSmallScreens">
            <p>{dayjs(workflow.updated).format("YYYY-MM-DD HH:mm")}</p>
          </div>
        </TableRow>
      )
    }

    return (
      <TableRow>
        <WorkflowIcon />
        <div>
          <Name>Workflow name</Name>
        </div>
        <div className="hideOnSmallScreens">
          <IdText>workflow-id</IdText>
        </div>
        <div>
          <TriggerBadge>ORDER_CREATED</TriggerBadge>
        </div>
        <div>
          <StatusBadge>...</StatusBadge>
        </div>
        <div className="hideOnSmallScreens">
          <ExecutionCount>...</ExecutionCount>
        </div>
        <div className="hideOnSmallScreens">
          <p>v1</p>
        </div>
        <div className="hideOnSmallScreens">
          <p>2026-01-01 00:00</p>
        </div>
      </TableRow>
    )
  }

  if (loading) {
    return <TableBlurLoading numberOfRows={4} tableHeader={tableHeader} rows={getTableRow()} />
  }

  return (
    <Wrapper>
      <Container>
        {error && (
          <ErrorMessage
            showRefreshButton
            message={
              <>
                Error loading workflows. Try refreshing the page, or contact{" "}
                <a href="mailto:support@brinkcommerce.com">support</a>.
              </>
            }
          />
        )}
        {workflows.length > 0 ? (
          <>
            {tableHeader}
            {workflows.map((workflow) => getTableRow(workflow))}
          </>
        ) : (
          <EmptyList>
            <WorkflowIcon />
            <p>No workflows found</p>
          </EmptyList>
        )}
      </Container>
    </Wrapper>
  )
}

export default WorkflowTable
